import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { Tenant } from "../../../../api/graphql";
import { TenantListStore } from "../../store";
import { tenantTypeDisplayId } from "../../../tenantType/store/TenantTypeCommonStore";
import { tenantStatusDisplayId } from "../../../tenantStatus/store/TenantStatusCommonStore";
import { tenantPlanDisplayId } from "../../../tenantPlan/store/TenantPlanCommonStore";
import { llmProviderDisplayId } from "../../../llmProvider/store/LlmProviderCommonStore";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: TenantListStore };

export const TenantListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<Tenant>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: Tenant, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: Tenant, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: Tenant) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: Tenant, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<Tenant>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "tenantType") {
          return {
            ...col,
            id: "tenantType",
            accessorFn: (row: Tenant) =>
              row.tenantType
                ? tenantTypeDisplayId(row.tenantType, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "status") {
          return {
            ...col,
            id: "status",
            accessorFn: (row: Tenant) =>
              row.status ? tenantStatusDisplayId(row.status, langStore) : "-",
          };
        }
        if (col.accessorKey === "plan") {
          return {
            ...col,
            id: "plan",
            accessorFn: (row: Tenant) =>
              row.plan ? tenantPlanDisplayId(row.plan, langStore) : "-",
          };
        }
        if (col.accessorKey === "lastTokenResetDate") {
          return {
            ...col,
            id: "lastTokenResetDate",
            accessorFn: (row: Tenant) =>
              row.lastTokenResetDate
                ? new Date(row.lastTokenResetDate).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "ragLlmProvider") {
          return {
            ...col,
            id: "ragLlmProvider",
            accessorFn: (row: Tenant) =>
              row.ragLlmProvider
                ? llmProviderDisplayId(row.ragLlmProvider, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "eSignatureProvider") {
          return {
            ...col,
            id: "eSignatureProvider",
            accessorFn: (row: Tenant) => {
              const v = (row as any).eSignatureProvider;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EsignatureProviderOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.tenantCurrentPage || [];

    return (
      <div id={"ListOfTenant"}>
        <div id={"StartOfListingOfTenant"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
